/**
 * A porta serial de um robô na tela de Ajustes: aberta uma vez, identificada
 * pelo `INFO`, e mantida aberta enquanto a tela estiver aberta (spec 3.4).
 * Reusa o leitor de linhas e a identificação da configuração pela USB
 * (`robo-usb/serial.ts` e `identificarRobo` de `robo-usb/fluxo.ts`): nada de
 * leitor paralelo.
 */
import { PRAZOS_PADRAO, identificarRobo, type Prazos } from '@/features/robo-usb/fluxo'
import type { InfoDoRobo } from '@/features/robo-usb/protocolo'
import { abrirLinhas, type Linhas, type PortaSerial } from '@/features/robo-usb/serial'
import type { PortaLike } from './canal'

/**
 * Na abertura espera o `LIVE:` do boot por até 6 s (o CP2102 reinicia a placa
 * ao abrir; placa sem essa ponte não imprime boot nenhum e segue no prazo).
 * A resposta ao `INFO` mantém o teto da configuração: o firmware só lê a USB
 * entre uma consulta HTTPS e outra.
 */
export const PRAZOS_DE_AJUSTES: Prazos = { ...PRAZOS_PADRAO, boot: 6_000 }

/** A fila de linhas que ninguém pediu tem teto: a porta fica aberta por horas. */
export const TETO_DA_FILA = 500

export type PortaDoRobo = PortaLike & {
  porta: PortaSerial
  linhas: Linhas | null
  info: InfoDoRobo | null
  /** Respondeu `CONFIG_INVALIDA` ao `INFO`: firmware anterior à identificação. */
  firmwareAntigo: boolean
}

export function embrulhar(porta: PortaSerial): PortaDoRobo {
  const p: PortaDoRobo = {
    porta, linhas: null, info: null, firmwareAntigo: false,
    async fechar() {
      const l = p.linhas
      p.linhas = null
      await l?.fechar()
    },
  }
  return p
}

export type OpcoesDaAbertura = {
  /** Toda linha lida, crua: o log ao vivo redige antes de guardar. */
  aoLer?: (linha: string) => void
  aoErroDeLeitura?: (erro: unknown) => void
  prazos?: Prazos
  agora?: () => number
  esperar?: (ms: number) => Promise<void>
  abrir?: typeof abrirLinhas
}

/**
 * O `abrirEIdentificar` de `canal.ts` para uma porta de verdade. `null` = a
 * porta não conta: não abriu, não respondeu, ou o firmware é anterior ao
 * `INFO` (`CONFIG_INVALIDA`) — e aí a tela diz "firmware antigo, atualize".
 * Quem chama decide fechar; a escolha de canal fecha as que não casaram.
 */
export async function abrirEIdentificar(
  p: PortaDoRobo, opcoes: OpcoesDaAbertura = {},
): Promise<{ mac: string } | null> {
  const abrir = opcoes.abrir ?? abrirLinhas
  if (!p.linhas) {
    p.linhas = await abrir(p.porta, 115200, opcoes.aoErroDeLeitura,
      { aoLer: opcoes.aoLer, teto: TETO_DA_FILA })
  }
  const resposta = await identificarRobo(
    p.linhas, opcoes.prazos ?? PRAZOS_DE_AJUSTES, opcoes.agora ?? Date.now,
    opcoes.esperar ?? ((ms) => new Promise((r) => setTimeout(r, ms))))
  p.info = resposta?.info ?? null
  p.firmwareAntigo = resposta !== undefined && resposta.info === null
  return p.info ? { mac: p.info.mac } : null
}
